import React from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Typography,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Box,
    FormControlLabel,
    Checkbox,
    LinearProgress,
    Alert,
    InputAdornment,
    Tooltip,
    Radio,
    RadioGroup
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import HelpIcon from '../help/HelpIcon';
import { DEFAULT_INGESTION_BATCH_SIZE, INGESTION_BATCH_HELP, INGESTION_BATCH_OPTIONS } from './ingestionBatchOptions';
import SnowflakeConnectionFields from './SnowflakeConnectionFields';

// File-based source connection types
const FILE_SOURCE_TYPES = new Set(['csv', 'excel', 'xlsx', 'xls']);

// Fase 3 (motor "Estándar", whisperbi-back-standar-ingestion) -- mismos 7
// tiers que ese repo despliega (256 MB descartado, ver su Fase 0: solo
// deja margen para archivos triviales). csvMaxMb/excelMaxMb son las
// estimaciones del diseño (overhead de baseline medido real, 175 MB;
// multiplicador de RAM pico estimado, pendiente de calibrar con archivos
// reales -- ver el documento de diseño).
// Fase 4 -- las cuentas sin plan de pago solo pueden usar el motor
// Estándar hasta este tier (256 MB no está desplegado). Solo UX: el control
// real lo hace file-ingestion contra whisperbi-credits-engine.
export const FREE_PLAN_MAX_MEMORY_TIER_MB = 512;

const STANDARD_ENGINE_MEMORY_TIERS = [
    { mb: 512, vcpu: 1, csvMaxMb: 23, excelMaxMb: 16 },
    { mb: 1024, vcpu: 1, csvMaxMb: 64, excelMaxMb: 43 },
    { mb: 1769, vcpu: 1, csvMaxMb: 124, excelMaxMb: 83 },
    { mb: 2048, vcpu: 1, csvMaxMb: 146, excelMaxMb: 98 },
    { mb: 3072, vcpu: 2, csvMaxMb: 228, excelMaxMb: 152 },
    { mb: 3538, vcpu: 2, csvMaxMb: 266, excelMaxMb: 177 },
    { mb: 4096, vcpu: 2, csvMaxMb: 310, excelMaxMb: 207 },
];

/**
 * Component for the data source form dialog (add/edit)
 */
const DataSourceFormDialog = ({
    open,
    onClose,
    onSave,
    formData,
    formErrors,
    isEditMode,
    handleInputChange,
    handleFileChange,
    isSaving,
    isSaveDisabled,
    onTestConnection,
    isTestingConnection,
    testConnectionResult,
    isPaidPlan = true,
    connectionId = null
}) => {
    // Derive whether this is a file-type connection
    const isFileType = FILE_SOURCE_TYPES.has(formData.connectionType);
    // Definir el estilo común para los campos de texto
    const textFieldStyle = {
        mb: 2,
        borderColor: 'text.secondary',
        '& .MuiOutlinedInput-root': {
            color: 'var(--wb-text-strong)',
            '& fieldset': {
                color: 'var(--wb-text-strong)',
                borderColor: 'var(--wb-outline)',
            },
            '&:hover fieldset': {
                borderColor: 'var(--wb-text-dim)',
            },
        },
        '& .MuiInputLabel-root': {
            color: 'var(--wb-text-soft)',
        },
        '& .MuiFormHelperText-root': {
            color: 'var(--wb-danger-strong)',
        },
    };

    // Definir el estilo común para los selects
    const selectStyle = {
        color: 'var(--wb-text-strong)',
        borderColor: 'text.secondary',
        '.MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--wb-outline)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--wb-primary)',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--wb-text-dim)',
        },
        '.MuiSvgIcon-root': {
            color: 'var(--wb-text-strong)',
        },
    };

    // No definimos handleFileChange aquí, debe venir como prop desde el componente padre
    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    bgcolor: 'var(--wb-bg-panel)',
                    color: 'var(--wb-text-strong)',
                    minWidth: '500px'
                }
            }}
        >
            <DialogTitle sx={{ borderBottom: '1px solid var(--wb-overlay-10)' }}>
                {isEditMode ? 'Editar Fuente de Datos' : 'Agregar Nueva Fuente de Datos'}
            </DialogTitle>
            <DialogContent sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {isEditMode
                        ? 'Modifica los datos de la conexión seleccionada.'
                        : 'Configura una nueva conexión a tu fuente de datos. Puedes conectarte a bases de datos y archivos.'}
                </Typography>

                {/* Campo para el nombre (común a todos los tipos) */}
                <TextField
                    required
                    margin="dense"
                    name="name"
                    label="Nombre"
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={formData.name}
                    onChange={handleInputChange}
                    error={formErrors.name}
                    helperText={formErrors.name ? "El nombre es obligatorio" : ""}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="nombre" />
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        ...textFieldStyle,
                        '& .MuiOutlinedInput-root': {
                            ...textFieldStyle['& .MuiOutlinedInput-root'],
                            '& fieldset': {
                                color: 'var(--wb-text-strong)',
                                borderColor: formErrors.name ? 'var(--wb-danger-strong)' : 'var(--wb-outline)',
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: formErrors.name ? 'var(--wb-danger-strong)' : 'var(--wb-text-soft)',
                        },
                    }}
                />

                {/* Selector de tipo de fuente */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
                <FormControl
                    fullWidth
                    margin="dense"
                    sx={{ mb: 0 }}
                    required
                    disabled={isEditMode}
                >
                    <InputLabel id="data-source-type-label" sx={{ color: 'var(--wb-text-soft)' }}>
                        Tipo de Fuente
                    </InputLabel>
                    <Select
                        labelId="data-source-type-label"
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        label="Tipo de Fuente"
                        sx={selectStyle}
                    >
                        <MenuItem value="database">Base de Datos (MySQL, PostgreSQL, etc.)</MenuItem>
                        {/* ERP y CRM aún no están implementados: solo se muestran al
                            editar una conexión vieja de ese tipo (el tipo no se puede cambiar). */}
                        {formData.type === 'erp' && <MenuItem value="erp">Sistema ERP</MenuItem>}
                        {formData.type === 'crm' && <MenuItem value="crm">Sistema CRM</MenuItem>}
                        <MenuItem value="file">Archivo Excel / CSV</MenuItem>
                    </Select>
                    {isEditMode && (
                        <Typography variant="caption" sx={{ color: 'var(--wb-text-dim)', mt: 0.5 }}>
                            El tipo de conexión no se puede cambiar después de crearla. Si necesitas otro tipo, elimina esta conexión y crea una nueva.
                        </Typography>
                    )}
                </FormControl>
                <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="tipo-de-fuente" sx={{ mt: 1 }} />
                </Box>

                {/* Campo oculto para el nombre del secreto */}
                {formData.secretName && (
                    <input
                        type="hidden"
                        name="secretName"
                        value={formData.secretName}
                    />
                )}

                {/* Campos específicos para base de datos */}
                {formData.type === 'database' && (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
                        <FormControl
                            fullWidth
                            margin="dense"
                            sx={{ mb: 0 }}
                        >
                            <InputLabel id="database-type-label" sx={{ color: 'var(--wb-text-soft)' }}>
                                Tipo de Base de Datos
                            </InputLabel>
                            <Select
                                labelId="database-type-label"
                                name="databaseType"
                                value={formData.databaseType}
                                onChange={handleInputChange}
                                label="Tipo de Base de Datos"
                                sx={selectStyle}
                            >
                                <MenuItem value="postgresql">PostgreSQL</MenuItem>
                                <MenuItem value="mysql">MySQL/MariaDB</MenuItem>
                                <MenuItem value="sqlserver">SQL Server</MenuItem>
                                <MenuItem value="oracle">Oracle</MenuItem>
                                <MenuItem value="redshift">Amazon Redshift</MenuItem>
                                <MenuItem value="snowflake">Snowflake</MenuItem>
                                <MenuItem value="bigquery">Google BigQuery</MenuItem>
                            </Select>
                        </FormControl>
                        <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="tipo-de-base-de-datos" sx={{ mt: 1 }} />
                        </Box>

                        {formData.databaseType === 'snowflake' ? (
                            // Snowflake: usuario de servicio + par de claves
                            // (sin contraseña), warehouse/rol/schema.
                            <SnowflakeConnectionFields
                                formData={formData}
                                formErrors={formErrors}
                                handleInputChange={handleInputChange}
                                isEditMode={isEditMode}
                                textFieldStyle={textFieldStyle}
                                connectionId={connectionId}
                            />
                        ) : (
                        <>
                            <TextField
                                required
                                margin="dense"
                                name="host"
                                label="Host/Servidor"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={formData.host}
                                onChange={handleInputChange}
                                error={formErrors.host}
                                helperText={formErrors.host ? "El host/servidor es obligatorio" : "Ejemplo: localhost, 192.168.1.100, db.example.com"}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="hostservidor" />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{
                                    ...textFieldStyle,
                                    '& .MuiOutlinedInput-root': {
                                        ...textFieldStyle['& .MuiOutlinedInput-root'],
                                        '& fieldset': {
                                            color: 'var(--wb-text-strong)',
                                            borderColor: formErrors.host ? 'var(--wb-danger-strong)' : 'var(--wb-outline)',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: formErrors.host ? 'var(--wb-danger-strong)' : 'var(--wb-text-soft)',
                                    },
                                }}
                            />

                            <TextField
                                required
                                margin="dense"
                                name="port"
                                label="Puerto"
                                type="number"
                                fullWidth
                                variant="outlined"
                                value={formData.port}
                                onChange={handleInputChange}
                                error={formErrors.port}
                                helperText={formErrors.port ? "El puerto es obligatorio" : "Puerto de conexión a la base de datos"}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="puerto" />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{
                                    ...textFieldStyle,
                                    '& .MuiOutlinedInput-root': {
                                        ...textFieldStyle['& .MuiOutlinedInput-root'],
                                        '& fieldset': {
                                            color: 'var(--wb-text-strong)',
                                            borderColor: formErrors.port ? 'var(--wb-danger-strong)' : 'var(--wb-outline)',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: formErrors.port ? 'var(--wb-danger-strong)' : 'var(--wb-text-soft)',
                                    },
                                }}
                            />

                            <TextField
                                required
                                margin="dense"
                                name="database"
                                label="Base de Datos"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={formData.database}
                                onChange={handleInputChange}
                                error={formErrors.database}
                                helperText={formErrors.database ? "El nombre de la base de datos es obligatorio" : ""}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="base-de-datos" />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{
                                    ...textFieldStyle,
                                    '& .MuiOutlinedInput-root': {
                                        ...textFieldStyle['& .MuiOutlinedInput-root'],
                                        '& fieldset': {
                                            color: 'var(--wb-text-strong)',
                                            borderColor: formErrors.database ? 'var(--wb-danger-strong)' : 'var(--wb-outline)',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: formErrors.database ? 'var(--wb-danger-strong)' : 'var(--wb-text-soft)',
                                    },
                                }}
                            />

                            <TextField
                                required
                                margin="dense"
                                name="username"
                                label="Usuario"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={formData.username}
                                onChange={handleInputChange}
                                error={formErrors.username}
                                helperText={formErrors.username ? "El usuario es obligatorio" : ""}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="usuario--contraseña" />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{
                                    ...textFieldStyle,
                                    '& .MuiOutlinedInput-root': {
                                        ...textFieldStyle['& .MuiOutlinedInput-root'],
                                        '& fieldset': {
                                            color: 'var(--wb-text-strong)',
                                            borderColor: formErrors.username ? 'var(--wb-danger-strong)' : 'var(--wb-outline)',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: formErrors.username ? 'var(--wb-danger-strong)' : 'var(--wb-text-soft)',
                                    },
                                }}
                            />

                            <TextField
                                required={!isEditMode}
                                margin="dense"
                                name="password"
                                label={isEditMode ? "Contraseña (dejar en blanco para no cambiar)" : "Contraseña"}
                                type="password"
                                fullWidth
                                variant="outlined"
                                value={formData.password}
                                onChange={handleInputChange}
                                error={formErrors.password}
                                helperText={formErrors.password ? "La contraseña es obligatoria" : ""}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="usuario--contraseña" />
                                        </InputAdornment>
                                    )
                                }}
                                sx={{
                                    ...textFieldStyle,
                                    '& .MuiOutlinedInput-root': {
                                        ...textFieldStyle['& .MuiOutlinedInput-root'],
                                        '& fieldset': {
                                            color: 'var(--wb-text-strong)',
                                            borderColor: formErrors.password ? 'var(--wb-danger-strong)' : 'var(--wb-outline)',
                                        },
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: formErrors.password ? 'var(--wb-danger-strong)' : 'var(--wb-text-soft)',
                                    },
                                }}
                            />

                            <TextField
                                margin="dense"
                                name="databaseVersion"
                                label="Versión de Base de Datos (opcional)"
                                type="text"
                                fullWidth
                                variant="outlined"
                                value={formData.databaseVersion}
                                onChange={handleInputChange}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="versión-de-base-de-datos-opcional" />
                                        </InputAdornment>
                                    )
                                }}
                                sx={textFieldStyle}
                            />
                        </>
                        )}
                    </>
                )}

                {/* Campos para ERP */}
                {formData.type === 'erp' && (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
                        <FormControl
                            fullWidth
                            margin="dense"
                            sx={{ mb: 0 }}
                        >
                            <InputLabel id="erp-type-label" sx={{ color: 'var(--wb-text-soft)' }}>
                                Tipo de ERP
                            </InputLabel>
                            <Select
                                labelId="erp-type-label"
                                name="erpType"
                                value={formData.erpType || ''}
                                onChange={handleInputChange}
                                label="Tipo de ERP"
                                sx={selectStyle}
                            >
                                <MenuItem value="sap">SAP</MenuItem>
                                <MenuItem value="oracle">Oracle ERP</MenuItem>
                                <MenuItem value="microsoft">Microsoft Dynamics</MenuItem>
                                <MenuItem value="sage">Sage</MenuItem>
                                <MenuItem value="odoo">Odoo</MenuItem>
                                <MenuItem value="infor">Infor</MenuItem>
                                <MenuItem value="netsuite">NetSuite</MenuItem>
                                <MenuItem value="other">Otro</MenuItem>
                            </Select>
                        </FormControl>
                        <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="tipo-de-erp" sx={{ mt: 1 }} />
                        </Box>

                        <TextField
                            required
                            margin="dense"
                            name="erpUrl"
                            label="URL de Conexión"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={formData.erpUrl || ''}
                            onChange={handleInputChange}
                            error={formErrors.erpUrl}
                            helperText={formErrors.erpUrl ? "La URL de conexión es obligatoria" : ""}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="url-de-conexión" />
                                    </InputAdornment>
                                )
                            }}
                            sx={textFieldStyle}
                        />

                        <TextField
                            required
                            margin="dense"
                            name="username"
                            label="Usuario"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={formData.username || ''}
                            onChange={handleInputChange}
                            error={formErrors.username}
                            helperText={formErrors.username ? "El usuario es obligatorio" : ""}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="usuario--contraseña-1" />
                                    </InputAdornment>
                                )
                            }}
                            sx={textFieldStyle}
                        />

                        <TextField
                            required={!isEditMode}
                            margin="dense"
                            name="password"
                            label={isEditMode ? "Contraseña (dejar en blanco para no cambiar)" : "Contraseña"}
                            type="password"
                            fullWidth
                            variant="outlined"
                            value={formData.password}
                            onChange={handleInputChange}
                            error={formErrors.password}
                            helperText={formErrors.password ? "La contraseña es obligatoria" : ""}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="usuario--contraseña-1" />
                                    </InputAdornment>
                                )
                            }}
                            sx={textFieldStyle}
                        />
                    </>
                )}

                {/* Campos para CRM */}
                {formData.type === 'crm' && (
                    <>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
                        <FormControl
                            fullWidth
                            margin="dense"
                            sx={{ mb: 0 }}
                        >
                            <InputLabel id="crm-type-label" sx={{ color: 'var(--wb-text-soft)' }}>
                                Tipo de CRM
                            </InputLabel>
                            <Select
                                labelId="crm-type-label"
                                name="crmType"
                                value={formData.crmType || ''}
                                onChange={handleInputChange}
                                label="Tipo de CRM"
                                sx={selectStyle}
                            >
                                <MenuItem value="salesforce">Salesforce</MenuItem>
                                <MenuItem value="hubspot">HubSpot</MenuItem>
                                <MenuItem value="dynamics">Microsoft Dynamics CRM</MenuItem>
                                <MenuItem value="zoho">Zoho CRM</MenuItem>
                                <MenuItem value="pipedrive">Pipedrive</MenuItem>
                                <MenuItem value="sugarcrm">SugarCRM</MenuItem>
                                <MenuItem value="other">Otro</MenuItem>
                            </Select>
                        </FormControl>
                        <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="tipo-de-crm" sx={{ mt: 1 }} />
                        </Box>

                        <TextField
                            required
                            margin="dense"
                            name="crmUrl"
                            label="URL de Conexión"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={formData.crmUrl || ''}
                            onChange={handleInputChange}
                            error={formErrors.crmUrl}
                            helperText={formErrors.crmUrl ? "La URL de conexión es obligatoria" : ""}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="url-de-conexión-1" />
                                    </InputAdornment>
                                )
                            }}
                            sx={textFieldStyle}
                        />

                        <TextField
                            required
                            margin="dense"
                            name="username"
                            label="Usuario/API Key"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={formData.username || ''}
                            onChange={handleInputChange}
                            error={formErrors.username}
                            helperText={formErrors.username ? "El usuario o API key es obligatorio" : ""}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="usuarioapi-key-y-contraseñaapi-secret" />
                                    </InputAdornment>
                                )
                            }}
                            sx={textFieldStyle}
                        />

                        <TextField
                            required={!isEditMode}
                            margin="dense"
                            name="password"
                            label={isEditMode ? "Contraseña/API Secret (dejar en blanco para no cambiar)" : "Contraseña/API Secret"}
                            type="password"
                            fullWidth
                            variant="outlined"
                            value={formData.password}
                            onChange={handleInputChange}
                            error={formErrors.password}
                            helperText={formErrors.password ? "La contraseña o API secret es obligatorio" : ""}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="usuarioapi-key-y-contraseñaapi-secret" />
                                    </InputAdornment>
                                )
                            }}
                            sx={textFieldStyle}
                        />
                    </>
                )}

                {/* Campos para archivo Excel / CSV */}
                {formData.type === 'file' && (
                    <>
                        <Box sx={{ mb: 2, mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                <Typography variant="subtitle1" sx={{ color: 'var(--wb-text-soft)' }}>
                                    Archivo Excel / CSV
                                </Typography>
                                <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="archivo" />
                            </Box>
                            <Box
                                sx={{
                                    border: '1px dashed var(--wb-x-border-ffffff4c, rgba(255, 255, 255, 0.3))',
                                    borderRadius: '4px',
                                    p: 3,
                                    textAlign: 'center',
                                    backgroundColor: 'var(--wb-x-bg-ffffff0d, rgba(255, 255, 255, 0.05))',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: 'var(--wb-overlay-08)',
                                        borderColor: 'var(--wb-text-dim)',
                                    }
                                }}
                                onClick={() => document.getElementById('excel-file-input').click()}
                            >
                                <input
                                    id="excel-file-input"
                                    type="file"
                                    accept=".xlsx,.xls,.csv"
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />
                                {formData.file ? (
                                    <>
                                        <Typography variant="body1" sx={{ color: 'var(--wb-text-strong)', mb: 1 }}>
                                            {formData.fileName}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'var(--wb-text-dim)' }}>
                                            Haga clic para cambiar el archivo
                                        </Typography>
                                    </>
                                ) : isEditMode && formData.fileName ? (
                                    <>
                                        <Typography variant="body2" sx={{ color: 'var(--wb-text-dim)', mb: 1 }}>
                                            Archivo actual:
                                        </Typography>
                                        <Typography variant="body1" sx={{ color: 'var(--wb-text-strong)', mb: 1 }}>
                                            {formData.fileName}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'var(--wb-text-dim)' }}>
                                            Haga clic para seleccionar un nuevo archivo
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <CloudUploadIcon sx={{ fontSize: 40, color: 'var(--wb-primary)', mb: 1 }} />
                                        <Typography variant="body1" sx={{ color: 'var(--wb-text-strong)', mb: 1 }}>
                                            Arrastre un archivo o haga clic para seleccionar
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'var(--wb-text-dim)' }}>
                                            Soporta archivos .xlsx, .xls y .csv
                                        </Typography>
                                    </>
                                )}
                            </Box>
                            {formErrors.file && (
                                <Typography variant="caption" sx={{ color: 'var(--wb-danger-strong)', mt: 1, display: 'block' }}>
                                    {formErrors.file}
                                </Typography>
                            )}
                            {/* File size warning (Requirement 7.8) */}
                            {formData.fileSize > 52428800 && (
                                <Alert severity="warning" sx={{ mt: 1 }}>
                                    El archivo no puede superar los 50 MB
                                </Alert>
                            )}
                            {/* Carga incremental: solo al volver a subir un archivo a
                                una conexión existente (la primera carga crea las tablas). */}
                            {isEditMode && formData.file && (
                                <Box sx={{ mt: 2, p: 1.5, border: '1px solid var(--wb-border-20)', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ color: 'var(--wb-text-strong)', fontWeight: 600, mb: 0.5 }}>
                                        Modo de carga
                                    </Typography>
                                    <RadioGroup
                                        name="loadMode"
                                        value={formData.loadMode || 'replace'}
                                        onChange={handleInputChange}
                                    >
                                        <FormControlLabel
                                            value="replace"
                                            control={<Radio size="small" />}
                                            label="Reemplazar datos: las tablas quedan con el contenido del archivo nuevo"
                                            sx={{ color: 'var(--wb-text-strong)' }}
                                        />
                                        <FormControlLabel
                                            value="append"
                                            control={<Radio size="small" />}
                                            label="Carga incremental: agregar las filas del archivo a las tablas existentes"
                                            sx={{ color: 'var(--wb-text-strong)' }}
                                        />
                                    </RadioGroup>
                                    {(formData.loadMode || 'replace') === 'append' && (
                                        <Alert severity="info" sx={{ mt: 1 }}>
                                            Cada hoja se agrega a la tabla del mismo nombre. Las columnas nuevas se agregan a
                                            la tabla y las que falten quedan vacías en las filas nuevas; un cambio de tipo
                                            incompatible queda en revisión de esquema. Sube solo datos nuevos: el mismo
                                            archivo no se puede agregar dos veces.
                                        </Alert>
                                    )}
                                </Box>
                            )}
                        </Box>

                        {/* Upload progress indicator (Requirement 7.4) */}
                        {formData.uploadState === 'uploading' && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)', mb: 1 }}>
                                    Subiendo archivo... {formData.uploadProgress}%
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={formData.uploadProgress || 0}
                                    sx={{ borderRadius: 1 }}
                                />
                            </Box>
                        )}

                        {/* Processing/confirming spinner (Requirement 7.4, 7.5) */}
                        {(formData.uploadState === 'confirming' || formData.uploadState === 'processing') && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <CircularProgress size={24} sx={{ color: 'var(--wb-primary)' }} />
                                <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)' }}>
                                    {formData.uploadState === 'confirming'
                                        ? 'Confirmando subida...'
                                        : 'Procesando archivo... Esto puede tomar unos minutos.'}
                                </Typography>
                            </Box>
                        )}

                        {/* Error alert (Requirement 7.5) */}
                        {formData.uploadState === 'error' && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {formData.errorMessage || 'Ocurrió un error durante la subida del archivo.'}
                            </Alert>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.hasHeaders || false}
                                        onChange={(e) => handleInputChange({
                                            target: {
                                                name: 'hasHeaders',
                                                value: e.target.checked
                                            }
                                        })}
                                        sx={{
                                            color: 'var(--wb-text-soft)',
                                            '&.Mui-checked': {
                                                color: 'var(--wb-primary)',
                                            },
                                        }}
                                    />
                                }
                                label="La primera fila contiene encabezados"
                                sx={{ color: 'var(--wb-text-strong)', mb: 1 }}
                            />
                            <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="la-primera-fila-contiene-encabezados" />
                        </Box>

                        {/* CSV delimiter selector (Requirement 7.7) */}
                        {formData.connectionType === 'csv' && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
                            <FormControl fullWidth margin="dense" sx={{ mb: 0 }}>
                                <InputLabel id="csv-delimiter-label" sx={{ color: 'var(--wb-text-soft)' }}>
                                    Delimitador
                                </InputLabel>
                                <Select
                                    labelId="csv-delimiter-label"
                                    name="csvDelimiter"
                                    value={formData.csvDelimiter || ','}
                                    onChange={handleInputChange}
                                    label="Delimitador"
                                    sx={selectStyle}
                                >
                                    <MenuItem value=",">Coma (,)</MenuItem>
                                    <MenuItem value=";">Punto y coma (;)</MenuItem>
                                    <MenuItem value={'\t'}>Tabulador</MenuItem>
                                </Select>
                            </FormControl>
                            <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="delimitador-solo-csv" sx={{ mt: 1 }} />
                            </Box>
                        )}

                        {/* Fase 3 -- selector de motor de ingesta */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 1 }}>
                            <FormControl fullWidth margin="dense" sx={{ mb: 0 }}>
                                <InputLabel id="upload-engine-label" sx={{ color: 'var(--wb-text-soft)' }}>
                                    Motor de ingesta
                                </InputLabel>
                                <Select
                                    labelId="upload-engine-label"
                                    name="uploadEngine"
                                    value={formData.uploadEngine || 'standard'}
                                    onChange={handleInputChange}
                                    label="Motor de ingesta"
                                    sx={selectStyle}
                                >
                                    <MenuItem value="standard">Estándar</MenuItem>
                                    <Tooltip
                                        title={isPaidPlan ? '' : 'Disponible en el plan de pago'}
                                        placement="right"
                                    >
                                        <span>
                                            <MenuItem value="spark" disabled={!isPaidPlan}>
                                                Spark
                                            </MenuItem>
                                        </span>
                                    </Tooltip>
                                </Select>
                            </FormControl>
                            <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="motor-de-ingesta" />
                        </Box>
                        <Typography variant="caption" sx={{ color: 'var(--wb-text-dim)', display: 'block', mb: 2 }}>
                            Estándar es recomendado para archivos menores a 300 MB. Spark es para archivos más grandes o cargas intensivas.
                        </Typography>

                        {(formData.uploadEngine || 'standard') === 'standard' ? (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
                                <FormControl fullWidth margin="dense" sx={{ mb: 0 }}>
                                    <InputLabel id="lambda-memory-mb-label" sx={{ color: 'var(--wb-text-soft)' }}>
                                        Nivel de memoria
                                    </InputLabel>
                                    <Select
                                        labelId="lambda-memory-mb-label"
                                        name="lambdaMemoryMb"
                                        value={formData.lambdaMemoryMb || (isPaidPlan ? 1024 : FREE_PLAN_MAX_MEMORY_TIER_MB)}
                                        onChange={handleInputChange}
                                        label="Nivel de memoria"
                                        sx={selectStyle}
                                    >
                                        {STANDARD_ENGINE_MEMORY_TIERS.map((tier) => {
                                            const maxMb = formData.connectionType === 'csv' ? tier.csvMaxMb : tier.excelMaxMb;
                                            return (
                                                <MenuItem
                                                    key={tier.mb}
                                                    value={tier.mb}
                                                    disabled={!isPaidPlan && tier.mb > FREE_PLAN_MAX_MEMORY_TIER_MB}
                                                >
                                                    {tier.mb} MB de RAM · ~{tier.vcpu} vCPU (hasta ~{maxMb} MB de archivo)
                                                    {!isPaidPlan && tier.mb > FREE_PLAN_MAX_MEMORY_TIER_MB ? ' · Plan de pago' : ''}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                                <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="nivel-de-memoria" />
                            </Box>
                        ) : (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                El cluster que se ejecutará tiene 32 GB de RAM y 8 vCPU (Glue G.2X, 2 DPU).
                            </Alert>
                        )}

                        {/* Hojas por bloque: solo Excel (un CSV es una sola tabla), al
                            crear o al volver a subir un archivo. */}
                        {formData.connectionType !== 'csv' && (!isEditMode || formData.file) && (
                            <Box sx={{ mb: 2 }}>
                                <FormControl fullWidth margin="dense" sx={{ mb: 0 }}>
                                    <InputLabel id="ingestion-batch-size-label" sx={{ color: 'var(--wb-text-soft)' }}>
                                        Procesar las hojas
                                    </InputLabel>
                                    <Select
                                        labelId="ingestion-batch-size-label"
                                        name="ingestionBatchSize"
                                        value={formData.ingestionBatchSize || DEFAULT_INGESTION_BATCH_SIZE}
                                        onChange={handleInputChange}
                                        label="Procesar las hojas"
                                        sx={selectStyle}
                                    >
                                        {INGESTION_BATCH_OPTIONS.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.sheetsLabel}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Typography variant="caption" sx={{ color: 'var(--wb-text-dim)', display: 'block', mt: 0.5 }}>
                                    {INGESTION_BATCH_HELP}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
                <FormControl
                    fullWidth
                    margin="dense"
                    sx={{ mb: 0 }}
                >
                    <InputLabel id="is-public-label" sx={{ color: 'var(--wb-text-soft)' }}>
                        Tipo de Acceso
                    </InputLabel>
                    <Select
                        labelId="is-public-label"
                        name="isPublic"
                        value={formData.isPublic}
                        onChange={handleInputChange}
                        label="Tipo de Acceso"
                        sx={{
                            color: 'var(--wb-text-strong)',
                            borderColor: 'text.secondary',
                            '.MuiOutlinedInput-notchedOutline': {
                                borderColor: 'var(--wb-outline)',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'var(--wb-primary)',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'var(--wb-text-dim)',
                            },
                            '.MuiSvgIcon-root': {
                                color: 'var(--wb-text-strong)',
                            },
                        }}
                    >
                        <MenuItem value={false}>Privado</MenuItem>
                        <MenuItem value={true}>Público</MenuItem>
                    </Select>
                </FormControl>
                <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="tipo-de-acceso" sx={{ mt: 1 }} />
                </Box>

                {formData.type === 'database' && (
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
                    <FormControl
                        fullWidth
                        margin="dense"
                        sx={{ mb: 0 }}
                    >
                        <InputLabel id="use-ssl-label" sx={{ color: 'var(--wb-text-soft)' }}>
                            Usar SSL
                        </InputLabel>
                        <Select
                            labelId="use-ssl-label"
                            name="useSsl"
                            value={formData.useSsl}
                            onChange={handleInputChange}
                            label="Usar SSL"
                            sx={{
                                color: 'var(--wb-text-strong)',
                                borderColor: 'text.secondary',
                                '.MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--wb-outline)',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--wb-primary)',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'var(--wb-text-dim)',
                                },
                                '.MuiSvgIcon-root': {
                                    color: 'var(--wb-text-strong)',
                                },
                            }}
                        >
                            <MenuItem value={true}>Sí</MenuItem>
                            <MenuItem value={false}>No</MenuItem>
                        </Select>
                    </FormControl>
                    <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="usar-ssl" sx={{ mt: 1 }} />
                    </Box>
                )}

                <TextField
                    margin="dense"
                    name="description"
                    label="Descripción (opcional)"
                    type="text"
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    value={formData.description}
                    onChange={handleInputChange}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="descripción-opcional" />
                            </InputAdornment>
                        )
                    }}
                    sx={{
                        mb: 2,
                        borderColor: 'text.secondary',
                        '& .MuiOutlinedInput-root': {
                            color: 'var(--wb-text-strong)',
                            '& fieldset': {
                                color: 'var(--wb-text-strong)',
                                borderColor: 'var(--wb-outline)',
                            },
                            '&:hover fieldset': {
                                color: 'var(--wb-text-strong)',
                                borderColor: 'var(--wb-text-dim)',
                            },
                        },
                        '& .MuiInputLabel-root': {
                            color: 'var(--wb-text-soft)',
                            borderColor: 'var(--wb-text-soft)',
                        },
                    }}
                />
            </DialogContent>
            <DialogActions sx={{ p: 2, borderTop: '1px solid var(--wb-overlay-10)', position: 'relative', display: 'flex', justifyContent: 'space-between' }}>
                {/* Overlay con loader cuando se está guardando */}
                {isSaving && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: 'var(--wb-x-bg-0000004c, rgba(0,0,0,0.3))',
                            zIndex: 10,
                            borderRadius: '0 0 4px 4px'
                        }}
                    >
                        <CircularProgress size={30} sx={{ color: 'var(--wb-primary)' }} />
                    </Box>
                )}

                {/* Botón de prueba de conexión (solo para bases de datos y en modo creación/edición) */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {formData.type === 'database' && onTestConnection && (
                        <Button
                            onClick={onTestConnection}
                            disabled={isSaving || isTestingConnection || (!(isEditMode && formData.databaseType === 'snowflake') && (!formData.host || !formData.username || !formData.database))}
                            variant="outlined"
                            sx={{
                                color: testConnectionResult?.success ? 'var(--wb-x-text-4caf50, #4caf50)' : 'var(--wb-primary)',
                                borderColor: testConnectionResult?.success ? 'var(--wb-x-border-4caf50, #4caf50)' : 'var(--wb-primary)',
                                '&:hover': { 
                                    borderColor: testConnectionResult?.success ? 'var(--wb-x-border-45a049, #45a049)' : 'var(--wb-primary-hover)',
                                    bgcolor: 'var(--wb-x-bg-0095ff14, rgba(0, 149, 255, 0.08))'
                                },
                                '&:disabled': {
                                    color: 'var(--wb-x-text-ffffff4c, rgba(255, 255, 255, 0.3))',
                                    borderColor: 'var(--wb-x-border-ffffff1f, rgba(255, 255, 255, 0.12))'
                                }
                            }}
                        >
                            {isTestingConnection ? (
                                <>
                                    <CircularProgress size={16} sx={{ mr: 1, color: 'var(--wb-primary)' }} />
                                    Probando...
                                </>
                            ) : testConnectionResult?.success ? (
                                '✓ Conexión exitosa'
                            ) : testConnectionResult?.success === false ? (
                                '✗ Conexión fallida'
                            ) : (
                                'Probar Conexión'
                            )}
                        </Button>
                    )}
                    {formData.type === 'database' && onTestConnection && (
                        <HelpIcon module="modelo-semantico" topic="fuente-de-datos" anchor="botón-probar-conexión" />
                    )}
                    {testConnectionResult?.message && (
                        <Typography 
                            variant="caption" 
                            sx={{ 
                                color: testConnectionResult.success ? 'var(--wb-x-text-4caf50, #4caf50)' : 'var(--wb-danger-strong)',
                                maxWidth: '200px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {testConnectionResult.message}
                        </Typography>
                    )}
                </Box>

                {/* Botones de acción */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={onClose}
                        disabled={isSaving}
                        sx={{ color: 'var(--wb-text-soft)', ":hover": { color: 'var(--wb-text-strong)', backgroundColor: 'var(--wb-overlay-08)' }, transition: 'color 0.2s' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={onSave}
                        disabled={isSaving}
                        variant="contained"
                        sx={{
                            bgcolor: 'var(--wb-primary)',
                            '&:hover': { bgcolor: 'var(--wb-primary-hover)' }
                        }}
                    >
                        {isEditMode ? 'Guardar Cambios' : 'Agregar conexión'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default DataSourceFormDialog;
